/*
 * ACCOUNT UTILITIES
 * 
 * Account-related utility functions and configurations for display and search.
 * 
 * Key Features:
 * - Account field definitions for search and display
 * - Separate configurations for search vs card display
 * - Account grid column definitions for table view
 * - Account filtering with customizable search fields
 * 
 * Functions:
 * - getAccountDisplayFields(): Returns all searchable account fields (includes name for search)
 * - getAccountCardDisplayFields(): Returns fields to display in account cards (excludes name since it's shown as card title)
 * - getAccountColumnDefs(): Generates ag-grid column definitions
 * - filterAccounts(): Filters account array by search query
 * 
 * Changes:
 * - Added getAccountCardDisplayFields() to separate card display from search fields
 * - name included in search but excluded from card details to avoid duplication
 */

import { Account } from '../models/Account';
import { ColDef, SortDirection, ValueGetterParams } from 'ag-grid-community';
import { createHighlightCellRenderer, createNameCellRenderer } from './gridCellRenderers';
import { type FormField } from '../components/common/FormField';
import { filterBySearchTerms } from './searchUtils';

export interface AccountField {
  key: keyof Account;
  label: string;
  hideEmpty?: boolean;
}

export const getAccountDisplayFields = (): AccountField[] => [
  { key: 'name', label: 'Name' },
  { key: 'streetAddress', label: 'Street', hideEmpty: true },
  { key: 'city', label: 'City', hideEmpty: true },
  { key: 'province', label: 'Province', hideEmpty: true },
  { key: 'postalCode', label: 'PostalCode', hideEmpty: true },
  { key: 'phone', label: 'Phone', hideEmpty: true },
  { key: 'noteCount', label: 'Notes' },
];

export const getAccountCardDisplayFields = (): AccountField[] => [
  { key: 'streetAddress', label: 'Street', hideEmpty: true },
  { key: 'city', label: 'City', hideEmpty: true },
  { key: 'province', label: 'Province', hideEmpty: true },
  { key: 'postalCode', label: 'PostalCode', hideEmpty: true },
  { key: 'phone', label: 'Phone', hideEmpty: true },
  { key: 'noteCount', label: 'Notes' },
];


export const getAccountColumnDefs = (searchQuery: string, schemaFields?: FormField[]): ColDef<Account>[] => {
  // Base columns that are always present
  const baseColumns = [
    {
      headerName: '',
      width: 50,
      maxWidth: 50,
      minWidth: 50,
      pinned: 'left' as const,
      cellStyle: { borderRight: '1px solid #ddd' },
      valueGetter: (params: ValueGetterParams<Account>) => {
        const rowIndex = params.node?.rowIndex;
        if (rowIndex === undefined || rowIndex === null) return 0;

        setTimeout(() => {
          params.api.refreshCells({
            rowNodes: [params.node!],
            columns: ['rowNumber'],
            force: true
          });
        }, 100);

        return rowIndex + 1;
      },
      colId: 'rowNumber',
      sortable: false,
      filter: false
    },
    {
      field: 'name',
      headerName: 'Name',
      sortable: true,
      width: 400,
      pinned: true,
      cellRenderer: createNameCellRenderer(searchQuery)
    },
    {
      field: 'noteCount',
      headerName: 'Note Count',
      sortable: true,
      sort: 'desc' as SortDirection,
      type: 'number'
    }
  ];

  if (!schemaFields || !schemaFields.length) return baseColumns;

  // Add schema-based columns
  const additionalColumns = schemaFields.map(field => {
    const columnDef: ColDef<Account> = {
      field: field.name,
      headerName: field.label,
      sortable: true,
      cellRenderer: createHighlightCellRenderer(searchQuery),
      filter: true,
      filterParams: {
        // Default text filter params for better UX
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
        defaultOption: 'contains',
        caseSensitive: false,
        suppressAndOrCondition: true
      }
    };

    // Set appropriate filter type based on field name
    if (['province', 'city'].includes(field.name)) {
      columnDef.type = 'text';
      columnDef.filter = 'agTextColumnFilter';
    } else if (field.name.toLowerCase().includes('count') || field.type === 'number') {
      columnDef.type = 'number';
      columnDef.filter = 'agNumberColumnFilter';
    }

    return columnDef;
  });

  return [...baseColumns, ...additionalColumns.filter(col => col.field !== 'name' && col.field !== 'noteCount')];
};

export const filterAccounts = (
  accounts: Account[],
  searchQuery: string,
  searchFields?: string[]
) => {
  return filterBySearchTerms(accounts, searchQuery, searchFields as Array<keyof Account>);
};
